import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Route } from 'react-router';

import { GET_LIST, UPDATE, authHeader, history, notify, provider } from '../_utils';
import { SearchClear, PcSelect } from '../_Components';
import Utils from './components/utils';
import MotivationModal from './components/MotivationModal';
import CalculateModal from './components/CalculateModal';
import PayrollTableModal from './components/PayrollTableModal';
import PaySheet from './PaySheet';
import PaySheetEdit from './PaySheetEdit';
import {
    TYPE_META,
    formatReportPeriod,
    fullName,
    initials,
    isTrainingOperator,
    operatorId,
    typeLabel,
} from './format';
import { formatHours, formatMoney, formatOcc, hoursFromSeconds, motivationLabel } from './motivation/formula';
import { applyBonusToSheetRow, buildSheetRow, downloadSheetExcel } from './motivation/sheet';
import './operators.css';

const { reportsStatus, operatorsTypes } = Utils.options();

const Icon = ({ d }) => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d={d} />
    </svg>
);

const defaultPeriod = () => moment().subtract(1, 'month').format('YYYY-MM');

const statusChip = (status) => {
    const map = {
        Advance: 'pc-emp-chip--warn',
        Approved: 'pc-emp-chip--acc',
        Accepted: 'pc-emp-chip--ok',
        Cenceled: 'pc-emp-chip--bad',
    };
    return map[status] || '';
};

const statusLabel = (status) =>
    reportsStatus.find((item) => item.id === status)?.name || status || '—';

const PAYROLL_TYPES = operatorsTypes.filter((item) => item.id !== 'test');

const occValue = (row) => row.calculation?.occPercent ?? row.occPercent ?? row.statistic?.occPercent;
const sumValue = (row) => row.calculation?.toPay ?? row.summa;
const hoursValue = (row) => {
    const calc = row.calculation || {};
    if (calc.payoutKind === 'contract' && calc.hoursPaid != null) return calc.hoursPaid;
    if (calc.hoursRecalc != null) return calc.hoursRecalc;
    if (calc.hoursPaid != null) return calc.hoursPaid;
    if (row.paidTime) return hoursFromSeconds(row.paidTime);
    return null;
};

const kindLabel = (row) => {
    const calc = row.calculation || {};
    const type = calc.contractType || row.contractType || row.operator?.contract?.type;
    if (calc.payoutKind === 'contract' || type === 'Contract') {
        const rate = calc.parttime || row.operator?.contract?.parttime;
        const rateName = rate === 'Half' ? '½' : rate === 'Quarter' ? '¼' : 'полная';
        return `Контракт · ${rateName}`;
    }
    if (type === 'Contractor' || calc.payoutKind === 'scheme') return 'Подряд';
    return '—';
};
const versionOf = (row) => row.motivation || row.motivationVersion || row.calculation?.motivation;

const OperatorsReportsList = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [period, setPeriod] = useState(defaultPeriod);
    const [type, setType] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [exporting, setExporting] = useState(false);
    const [openMotivation, setOpenMotivation] = useState(false);
    const [openCalculate, setOpenCalculate] = useState(false);
    const [openSheet, setOpenSheet] = useState(false);
    const [sheetRows, setSheetRows] = useState([]);
    const [sheetLoading, setSheetLoading] = useState(false);
    const [calculatingPeriod, setCalculatingPeriod] = useState(null);
    const sheetRowsRef = useRef([]);
    const bonusSaveTimers = useRef({});

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedQuery(query.trim()), 300);
        return () => clearTimeout(timer);
    }, [query]);

    const load = useCallback(async () => {
        const filter = {
            period: moment(period, 'YYYY-MM').startOf('month').toISOString(),
        };
        if (status) filter.status = status;
        if (type) filter.type = type;
        if (debouncedQuery) {
            if (/^[a-z]+\d*$/i.test(debouncedQuery)) filter.login = debouncedQuery;
            else filter.fullname = debouncedQuery;
        }
        try {
            const { data, total: count } = await provider(GET_LIST, '/operators/reports', {
                filter,
                sort: { field: 'fullname', order: 'ASC' },
                pagination: { page, perPage },
            });
            const visible = (data || []).filter((row) => !isTrainingOperator(row));
            setRows(visible);
            setTotal(typeof count === 'number' ? count : visible.length);
        } catch (error) {
            console.error('Error loading operator reports:', error);
        }
    }, [period, status, type, debouncedQuery, page, perPage]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [period, status, type, debouncedQuery, perPage]);

    const loadSheetRows = async () => {
        const filter = {
            period: moment(period, 'YYYY-MM').startOf('month').toISOString(),
        };
        if (status) filter.status = status;
        if (type) filter.type = type;
        if (debouncedQuery) {
            if (/^[a-z]+\d*$/i.test(debouncedQuery)) filter.login = debouncedQuery;
            else filter.fullname = debouncedQuery;
        }
        const { data } = await provider(GET_LIST, '/operators/reports', {
            filter,
            sort: { field: 'fullname', order: 'ASC' },
            pagination: { page: 1, perPage: 5000 },
        });
        return (data || []).filter((row) => !isTrainingOperator(row)).map(buildSheetRow);
    };

    const openTable = async () => {
        setOpenSheet(true);
        setSheetLoading(true);
        try {
            const next = await loadSheetRows();
            sheetRowsRef.current = next;
            setSheetRows(next);
        } catch (error) {
            notify({ message: 'Не удалось загрузить таблицу расчёта', type: 'error' });
            sheetRowsRef.current = [];
            setSheetRows([]);
        } finally {
            setSheetLoading(false);
        }
    };

    const exportReport = async () => {
        setExporting(true);
        try {
            const rowsForFile = openSheet && sheetRows.length ? sheetRows : await loadSheetRows();
            if (!rowsForFile.length) {
                notify({ message: 'Нет данных для выгрузки. Сначала запустите расчёт.', type: 'warning' });
                return;
            }
            downloadSheetExcel(rowsForFile, period);
        } catch (error) {
            notify({ message: 'Не удалось скачать Excel', type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const saveSheetBonus = async (id) => {
        const row = sheetRowsRef.current.find((item) => item.id === id);
        if (!row) return;
        const { status, message } = await provider(UPDATE, '/operators/reports', {
            id,
            data: {
                calculation: {
                    bonusPercent: row.bonusPercent,
                    bonusPay: row.bonusPay,
                    schemeTotal: row.schemeTotal,
                    toPay: row.toPay,
                    bonusManual: true,
                    basePay: row.basePay,
                    allowance: row.allowance,
                    contractPay: row.contractPay,
                    payoutKind: row.payoutKind,
                    hoursRecalc: row.hoursRecalc,
                    hoursPaid: row.hoursPaid,
                },
                summa: row.toPay,
                premia: row.bonusPay,
            },
        });
        if (status === 200) {
            load();
        } else {
            notify({ message: message || 'Не удалось сохранить премию', type: 'error' });
        }
    };

    const changeSheetBonus = (id, patch) => {
        setSheetRows((current) => {
            const next = current.map((row) => (row.id === id ? applyBonusToSheetRow(row, patch) : row));
            sheetRowsRef.current = next;
            return next;
        });
        window.clearTimeout(bonusSaveTimers.current[id]);
        bonusSaveTimers.current[id] = window.setTimeout(() => saveSheetBonus(id), 600);
    };

    const commitSheetBonus = (id) => {
        window.clearTimeout(bonusSaveTimers.current[id]);
        saveSheetBonus(id);
    };

    const startCalculate = async ({ period: nextPeriod, motivationId, regenerate, normFullHours }) => {
        setOpenCalculate(false);
        setCalculatingPeriod(nextPeriod);
        notify({
            message: `Идёт расчёт за ${formatReportPeriod(nextPeriod)}`,
            type: 'info',
        });
        try {
            const response = await fetch('/api/operators/reports/calculate', {
                credentials: 'same-origin',
                method: 'POST',
                headers: { ...authHeader(), 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    period: nextPeriod,
                    motivationId: motivationId || undefined,
                    regenerate,
                    normFullHours,
                }),
            });
            if (response.ok) {
                notify({ message: `Расчёт за ${formatReportPeriod(nextPeriod)} завершён`, type: 'success' });
                if (nextPeriod !== period) setPeriod(nextPeriod);
                else await load();
            } else {
                const payload = await response.json().catch(() => ({}));
                notify({
                    message: payload.message || 'Не удалось выполнить расчёт. Проверьте, что API расчёта реализован.',
                    type: 'error',
                });
            }
        } catch (error) {
            notify({ message: 'Ошибка сети при запуске расчёта', type: 'error' });
        } finally {
            setCalculatingPeriod(null);
        }
    };

    const pages = Math.max(1, Math.ceil(total / perPage) || 1);

    return (
        <Fragment>
            <div className="pc-page pc-emp">
                <div className="pc-emp-topbar">
                    <button type="button" className="pc-emp-btn" onClick={() => history.push('/operators')}>
                        <Icon d="M13 8H4M7.5 4.5L4 8l3.5 3.5" />
                        Назад
                    </button>
                    <h1>Расчёты ЗП</h1>
                    <input
                        className="pc-emp-inp pc-emp-inp--period"
                        type="month"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        title="Период"
                    />
                    <div className="pc-emp-srch">
                        <Icon d="M7 2.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM10.5 10.5L14 14" />
                        <input
                            className="pc-emp-inp"
                            value={query}
                            placeholder="Поиск по имени или логину"
                            onChange={(e) => setQuery(e.target.value)}
                        />
                        <SearchClear visible={Boolean(query)} onClear={() => setQuery('')} />
                    </div>
                    <PcSelect
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        options={PAYROLL_TYPES}
                        allowEmpty
                        emptyLabel="Все типы"
                        searchable={false}
                    />
                    <PcSelect
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        options={reportsStatus}
                        allowEmpty
                        emptyLabel="Все статусы"
                        searchable={false}
                    />
                    <span className="pc-emp-chip pc-emp-chip--mono">
                        {total
                            ? `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} из ${total}`
                            : '0 расчётов'}
                    </span>
                    <div className="pc-emp-end">
                        <button type="button" className="pc-emp-btn" onClick={() => setOpenMotivation(true)}>
                            <Icon d="M2.5 8h11M8 2.5v11M4.5 4.5l7 7M11.5 4.5l-7 7" />
                            Мотивация
                        </button>
                        <button type="button" className="pc-emp-btn" onClick={openTable}>
                            Таблица
                        </button>
                        <button type="button" className="pc-emp-btn" disabled={exporting} onClick={exportReport}>
                            Скачать Excel
                        </button>
                        <button
                            type="button"
                            className="pc-emp-btn pc-emp-btn--pri"
                            disabled={Boolean(calculatingPeriod)}
                            onClick={() => setOpenCalculate(true)}
                        >
                            {calculatingPeriod ? 'Считаем…' : 'Рассчитать ЗП'}
                        </button>
                    </div>
                </div>

                {calculatingPeriod ? (
                    <div className="pc-pay-banner">
                        Идёт расчёт за {formatReportPeriod(calculatingPeriod)}. Список обновится после завершения.
                    </div>
                ) : null}

                <div className="pc-emp-stage">
                    <div className="pc-emp-panel">
                        <div className="pc-emp-thead pc-pay-head">
                            <span className="pc-emp-c-emp">Сотрудник</span>
                            <span className="pc-pay-c-kind">Договор</span>
                            <span className="pc-pay-c-period">Период</span>
                            <span className="pc-pay-c-hours">Часы</span>
                            <span className="pc-pay-c-occ">OCC</span>
                            <span className="pc-pay-c-prem">Премия</span>
                            <span className="pc-pay-c-sum">Сумма</span>
                            <span className="pc-pay-c-ver">Версия мотивации</span>
                            <span className="pc-pay-c-st">Статус</span>
                            <span className="pc-emp-c-go" />
                        </div>
                        <div className="pc-emp-pbd">
                            {rows.length === 0 ? (
                                <div className="pc-emp-empty">
                                    <b>Нет расчётных листов</b>
                                    <p>Выберите период и нажмите «Рассчитать ЗП». Расчёт не запускается сам — только по кнопке.</p>
                                </div>
                            ) : rows.map((row) => {
                                const id = operatorId(row);
                                const name = fullName(row) || fullName(row.operator) || '';
                                const login = row.login || row.operator?.login || '';
                                const meta = TYPE_META[row.type || row.operator?.type] || TYPE_META.staf;
                                const premia = row.calculation?.bonusPay ?? row.premia;
                                const version = versionOf(row);
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        className="pc-emp-trow pc-pay-head"
                                        onClick={() => history.push(`/operators/reports/${id}/show`)}
                                    >
                                        <span className="pc-emp-c-emp">
                                            <span className={`pc-emp-av pc-emp-av-${meta.av}`}>{initials(name || login)}</span>
                                            <span style={{ minWidth: 0 }}>
                                                <span className="n">{name || '—'}</span>
                                                <span className="s">{login || '—'} · {typeLabel(row.type || row.operator?.type)}</span>
                                            </span>
                                        </span>
                                        <span className="pc-pay-c-kind">{kindLabel(row)}</span>
                                        <span className="pc-pay-c-period">{formatReportPeriod(row.period || period)}</span>
                                        <span className="pc-pay-c-hours">{hoursValue(row) != null ? formatHours(hoursValue(row)) : '—'}</span>
                                        <span className="pc-pay-c-occ">{occValue(row) != null ? formatOcc(occValue(row)) : '—'}</span>
                                        <span className="pc-pay-c-prem">{premia != null && premia !== '' ? formatMoney(premia) : '—'}</span>
                                        <span className="pc-pay-c-sum">{sumValue(row) != null ? formatMoney(sumValue(row)) : '—'}</span>
                                        <span className="pc-pay-c-ver">
                                            {motivationLabel(version) ? (
                                                <span className="pc-emp-chip pc-emp-chip--mono">{motivationLabel(version)}</span>
                                            ) : (
                                                <span className="pc-emp-dim">нет версии</span>
                                            )}
                                        </span>
                                        <span className="pc-pay-c-st">
                                            <span className={`pc-emp-chip ${statusChip(row.status)}`}>
                                                <span className="pc-emp-dot" />
                                                {statusLabel(row.status)}
                                            </span>
                                        </span>
                                        <span className="pc-emp-c-go">
                                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                                <path d="M6 3.5L10.5 8 6 12.5" />
                                            </svg>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="pc-emp-pager">
                            <PcSelect
                                value={String(perPage)}
                                onChange={(e) => setPerPage(Number(e.target.value))}
                                options={[25, 50, 100].map((n) => ({ id: String(n), name: `${n} на странице` }))}
                                searchable={false}
                            />
                            <span className="pc-emp-pager__n">
                                {total ? `${(page - 1) * perPage + 1}–${Math.min(page * perPage, total)} из ${total}` : '0 из 0'}
                            </span>
                            <span className="pc-emp-pager__n">{page} / {pages}</span>
                            <div className="pc-emp-navb">
                                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M10 3.5L5.5 8l4.5 4.5" />
                                    </svg>
                                </button>
                                <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                        <path d="M6 3.5L10.5 8 6 12.5" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Route path="/operators/reports/:id/:action">
                {({ match }) => {
                    const isMatch = !!(match && match.params);
                    if (!isMatch) return null;
                    if (match.params.action === 'edit') {
                        return (
                            <div className="pc-emp-ov" onClick={(event) => { if (event.target === event.currentTarget) history.push('/operators/reports'); }}>
                                <div className="pc-emp-modal pc-emp-modal--xl">
                                    <PaySheetEdit id={match.params.id} />
                                </div>
                            </div>
                        );
                    }
                    return (
                        <PaySheet id={match.params.id} onSaved={load} />
                    );
                }}
            </Route>

            <PayrollTableModal
                open={openSheet}
                period={period}
                rows={sheetRows}
                loading={sheetLoading}
                onClose={() => setOpenSheet(false)}
                onOpenRow={(id) => {
                    setOpenSheet(false);
                    history.push(`/operators/reports/${id}/show`);
                }}
                onChangeBonus={changeSheetBonus}
                onSaveBonus={commitSheetBonus}
            />
            <MotivationModal open={openMotivation} onClose={() => setOpenMotivation(false)} />
            <CalculateModal
                open={openCalculate}
                period={period}
                onClose={() => setOpenCalculate(false)}
                onStart={startCalculate}
            />
        </Fragment>
    );
};

export default OperatorsReportsList;
